import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, HeartCrack, Loader2, LogIn } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * iter122 — "Save this festival" toggle on /festivals/:slug.
 *
 * Pins the festival to the user's profile and queues them for the
 * 14-day-before reminder email (server-side daily loop in
 * routers/festivals.send_festival_reminders).
 *
 * Anon users see a "Sign in to save" CTA — we don't try to support
 * anon-saved festivals because the reminder email obviously needs an
 * email address attached.
 */
export default function SaveFestivalButton({ slug, festivalName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  // On mount: if the user is signed in, ask "have you already saved this?"
  useEffect(() => {
    if (!user) {
      setChecked(true);
      return;
    }
    let alive = true;
    api.get("/me/festivals").then(({ data }) => {
      if (!alive) return;
      const isSaved = (data?.festivals || []).some((f) => f.slug === slug);
      setSaved(isSaved);
    }).catch(() => { /* anon or error — treat as not saved */ })
    .finally(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, [user, slug]);

  const toggle = async () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/festivals/${slug}`)}`);
      return;
    }
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/festivals/${slug}/save`);
        setSaved(false);
        toast.success(`Removed ${festivalName} from your saved festivals`);
      } else {
        await api.post(`/festivals/${slug}/save`);
        setSaved(true);
        toast.success(`Saved · we'll email you 14 days before ${festivalName}`);
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#0e0e0e] border border-white hover:bg-[#fafafa] transition-colors text-xs mono uppercase tracking-wider"
        data-testid="festival-save-cta-login"
        title="Sign in to save this festival and get a reminder 14 days before"
      >
        <LogIn className="w-3.5 h-3.5" />
        Save · sign in
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy || !checked}
      className={`inline-flex items-center gap-2 px-4 py-2 transition-colors text-xs mono uppercase tracking-wider border ${
        saved
          ? "bg-white text-[#dc2626] border-white hover:bg-[#fff5f5]"
          : "bg-white text-[#0e0e0e] border-white hover:bg-[#fafafa]"
      } disabled:opacity-60`}
      data-testid={saved ? "festival-saved-btn" : "festival-save-btn"}
      title={saved ? "Saved — we'll email you 14 days before" : "Save this festival + get a 14-day reminder"}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : saved ? (
        <Heart className="w-3.5 h-3.5 fill-current" />
      ) : (
        <Heart className="w-3.5 h-3.5" />
      )}
      {saved ? "Saved · reminder set" : "Save festival"}
    </button>
  );
}
